import React from 'react'
import { Table, Tag, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'

interface DayRow {
  date: string
  count: number
  valorNewDriverSum: number
  isWeekend: boolean
  isHoliday: boolean
  holidayNames: string[]
}

interface Props {
  days: DayRow[]
  loading: boolean
}

const CELL_PADDING = '7px 16px'
const BAR_COLOR_COUNT = '#52c41a'
const BAR_COLOR_VALOR = '#1677ff'

export function EvolucaoDiariaTable({ days, loading }: Props) {
  const maxCount = Math.max(...days.map((d) => d.count), 0)
  const maxValor = Math.max(...days.map((d) => d.valorNewDriverSum), 0)

  const columns: ColumnsType<DayRow> = [
    {
      title: 'Data',
      dataIndex: 'date',
      render: (date) => new Date(date + 'T00:00:00').toLocaleDateString('pt-BR'),
    },
    {
      title: 'Dia',
      dataIndex: 'date',
      render: (date) => DAY_NAMES[new Date(date + 'T00:00:00').getDay()],
    },
    {
      title: 'Obs.',
      render: (_, row) => (
        <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {row.isWeekend && <Tag color="purple">FDS</Tag>}
          {row.isHoliday && (
            <Tooltip title={row.holidayNames.join(', ')}>
              <Tag color="orange">Feriado</Tag>
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: 'Matrículas',
      dataIndex: 'count',
      onCell: () => ({ style: { padding: 0 } }),
      render: (value) => (
        <BarCell
          value={value}
          max={maxCount}
          formatted={String(value)}
          color={BAR_COLOR_COUNT}
          padding={CELL_PADDING}
        />
      ),
    },
    {
      title: 'Receita NewDriver',
      dataIndex: 'valorNewDriverSum',
      onCell: () => ({ style: { padding: 0 } }),
      render: (value: number) => (
        <BarCell
          value={value}
          max={maxValor}
          formatted={new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)}
          color={BAR_COLOR_VALOR}
          padding={CELL_PADDING}
        />
      ),
    },
  ]

  return (
    <Table
      columns={columns}
      dataSource={days}
      loading={loading}
      rowKey="date"
      pagination={false}
      rowClassName={(row) => (row.isWeekend || row.isHoliday ? 'holiday-week-row' : '')}
    />
  )
}

function BarCell({
  value,
  max,
  formatted,
  color,
  padding,
}: {
  value: number
  max: number
  formatted: string
  color: string
  padding: string
}) {
  const pct = max > 0 ? (value / max) * 100 : 0
  return (
    <div style={{ position: 'relative', padding, minHeight: 22 }}>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: `${pct}%`,
          background: color,
          border: '1px solid #d9d9d9',
          borderRadius: 4,
          zIndex: 1,
        }}
      />
      <span style={{ position: 'relative', zIndex: 0 }}>{formatted}</span>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: `${pct}%`,
          overflow: 'hidden',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      >
        <span style={{ display: 'block', padding, color: '#fff', whiteSpace: 'nowrap' }}>{formatted}</span>
      </div>
    </div>
  )
}

const DAY_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
